import React, { useState, useRef } from 'react'
import { arrayOf, func, shape } from 'prop-types'
import {
  DialogOverlay,
  TextField,
  DialogOverlayContent,
  Body2,
  Body1,
  useOverlay,
  Button,
  useAtSize,
} from '@fs/zion-ui'
import { MenuSearch } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { expandTileData, useAamDispatch, useAamState } from '../../state/AamStore'
import useDSApplicationMode from '../../../../shared/hooks/useDSApplicationMode'
import YearPickerAncestors from './YearPickerAncestors/YearPickerAncestors'
import MissingBirthDateDialog from '../MissingBirthDateDialog/MissingBirthDateDialog'
import { newAncestorTemplate } from '../../../../shared/helpers/person_Helper'

const yearInputStyle = css`
  display: flex;
  align-items: center;
  z-index: 4;
  justify-content: stretch;
  width: 200px;
  margin: 0 auto;
`

const centerWrapper = css`
  display: flex;
  justify-content: center;
`

const centerDescription = css`
  margin-top: 20px;
  max-width: 460px;
  text-align: center;
`
const buttonWrapper = css`
  display: flex;
  justify-content: center;
`
const discoveryCenterPadding = css`
  padding: 40px;
`

export default function YearPicker({ overlay, tileData, showAncestorList }) {
  const [t] = useTranslation()
  const dispatch = useAamDispatch()
  const state = useAamState()
  const atSize = useAtSize()

  const inWebMode = useDSApplicationMode() === 'WEB'
  const inputRef = useRef()

  const [yearQuery, setYearQuery] = useState('')
  const missingBirthDateOverlay = useOverlay()
  const [activePerson, setActivePerson] = useState()
  const [submitEnabled, setSubmitEnabled] = useState()

  const yearPickerStyles = css`
    width: ${atSize({ default: '100%', lg: '80vw' })};
  `

  const checkForDuplicates = (person) => {
    const ancestorTiles = tileData.filter((tile) => tile.type === 'ancestorDetails')
    const foundDuplicate = ancestorTiles.some((personTile) => personTile.header !== person.header)
    return foundDuplicate
  }

  const mapAncestorsToNewYear = (year, region, language) => {
    const ancestorTiles = []
    state.tree.forEach((person) => {
      const personYears = person?.lifespan.split('-')
      const n = `${person?.name} ${person?.surname}`
      if (parseInt(personYears[0], 10) === parseInt(year, 10) && !checkForDuplicates(person)) {
        ancestorTiles.push(newAncestorTemplate(year, person, region, language, n, t('aam.year-picker.ancestor-birth')))
      } else if (parseInt(personYears[1], 10) === year && !checkForDuplicates(person)) {
        ancestorTiles.push(newAncestorTemplate(year, person, region, language, n, t('aam.year-picker.ancestor-death')))
      }
    })
    return ancestorTiles
  }

  const removeNonMatchingAncestors = (year) => {
    // Need traverse the array backwards while removing items so the indexes don't get out of place
    let tileIndex = tileData.length
    while (tileIndex--) {
      if (tileData[tileIndex].type === 'ancestorDetails' && tileData[tileIndex].year !== year) {
        tileData.splice(tileIndex, 1)
      }
    }
  }

  const setYearInfo = (year, person = state.person) => {
    const firstName = person?.name ? person.name : state.firstName
    const lastName = person?.surname ? person.surname : state.lastName
    const personId = person?.personId
    const gender = person?.gender
    const atn = person?.atn

    removeNonMatchingAncestors(year)

    const { region, language } = tileData[0]

    const ancestorYearMatches = mapAncestorsToNewYear(year, region, language)

    if (ancestorYearMatches.length !== 0) {
      ancestorYearMatches.forEach((match) => tileData.push(match))
    }

    const tileDataRefresh = expandTileData({
      tileData,
      firstName,
      lastName,
      year,
      personId,
      gender,
      atn,
    })

    dispatch({
      type: 'UPDATE_TILE_DATA',
      data: { tileData: tileDataRefresh, year, firstName, lastName },
    })

    overlay.close()
  }

  const validYearCheck = () => {
    if (
      (yearQuery.length === 4 && yearQuery >= 1900 && yearQuery <= new Date().getFullYear()) ||
      yearQuery.length === 0
    ) {
      if (yearQuery.length === 0) {
        return null
      }
      setSubmitEnabled(true)
      return null
    }
    setSubmitEnabled(false)
    if (yearQuery < 1900 && yearQuery.length === 4) {
      return t('aam.year-picker.error.birth-year-before-1900', 'Please enter a birth year after 1900')
    }
    return t('aam.year-picker.error.birth-year-invalid', 'Please enter a valid birth year')
  }

  const handleYearQueryUpdate = (e) => {
    setYearQuery(e.target.value)
    validYearCheck()
  }

  const personTileClickHandler = (person) => {
    if (!person.dob || person.dob === 'Living') {
      setActivePerson(person)
      missingBirthDateOverlay.handleClick()
      return
    }

    setYearInfo(parseInt(person.dob, 10), person)
  }

  const DescriptionType = inWebMode ? Body2 : Body1

  return (
    <>
      <DialogOverlay {...overlay} headingText={t('aam.year-picker.view-year', 'View A Different Year')} autoWidth>
        <DialogOverlayContent>
          <div css={[!inWebMode ? discoveryCenterPadding : '', yearPickerStyles]}>
            <div css={yearInputStyle}>
              <TextField
                Icon={MenuSearch}
                value={yearQuery}
                label={t('aam.year-picker.enter-year', 'Enter a new year')}
                type="number"
                ref={inputRef}
                onChange={(e) => handleYearQueryUpdate(e)}
                warningValidate={validYearCheck}
              />
            </div>

            <div css={buttonWrapper}>
              <Button
                type="submit"
                external="false"
                data-testid="submit"
                emphasis="billboard"
                color="blue"
                disabled={!submitEnabled}
                onClick={() => {
                  setYearInfo(yearQuery, state.person)
                }}
              >
                {t('common-ui:submit.action', 'Submit')}
              </Button>
            </div>
            <div css={centerWrapper}>
              <div css={centerDescription}>
                <DescriptionType>{t('aam.year-picker.description')}</DescriptionType>
              </div>
            </div>
            {showAncestorList && <YearPickerAncestors personTileClickHandler={personTileClickHandler} />}
          </div>
        </DialogOverlayContent>
      </DialogOverlay>
      {activePerson && (
        <MissingBirthDateDialog
          onUpdate={(person) => personTileClickHandler(person)}
          overlay={missingBirthDateOverlay}
          person={activePerson}
        />
      )}
    </>
  )
}

YearPicker.propTypes = {
  overlay: shape({
    close: func,
  }).isRequired,
  tileData: arrayOf(shape({})),
}
